import { Destination2 } from '~/models/Destination'
import {
	Itinerary2 as Itinerary,
	Itinerary2,
	TRIP_PHASE,
	TRIP_TASK_STATE,
	TripOptions,
	TripTask,
} from '~/models/Itinerary'
import ItineraryGroup from '~/models/ItineraryGroup'
import { ItinerarySpot2 as ItinerarySpot } from '~/models/ItinerarySpot'
import { Marker2 } from '~/models/Marker'
import Media from '~/models/Media'
import { Page } from '~/models/PaginatedItems'
import { Spot2 } from '~/models/Spot'
import { Traveler2 } from '~/models/Traveler'
import { ItineraryApiMapper } from '~/passporter-services/itinerary/apiMapper'
import TextValidator from '~/passporter-services/shared/textValidator'
import { ItineraryEntityMapper } from './entityMapper'
import { ItineraryRepository } from './repository'
import { ItineraryDay } from '~/models/ItineraryDay'
import { ORDER } from '~/models/Order'
import Product, { PRODUCT_ORDER, ProductFilters } from '~/models/Product'

export class ItineraryService {
	repository: any
	constructor(repository: any) {
		this.repository = repository
	}

	static async deleteItinerary(itineraryId: string) {
		await ItineraryRepository.deleteItinerary(itineraryId)
	}

	static async createItinerary(itinerary: Itinerary2, recommendedSpots?: boolean, origin?: string) {
		const apiForm = ItineraryApiMapper.toItineraryCreationForm(itinerary, recommendedSpots, origin)
		const response = await ItineraryRepository.createItinerary(apiForm)
		return response
	}

	static async validateItinerary(
		itinerary: Itinerary2
	): Promise<Partial<Record<keyof Itinerary2, string>> | undefined> {
		const itineraryErrors: Partial<Record<keyof Itinerary2, string>> = {}

		itineraryErrors.name = this.retrieveItineraryNameErrors(itinerary)
		itineraryErrors.destinations = this.retrieveItineraryDestinationsErrors(itinerary)

		const hasErrors = Object.values(itineraryErrors).some((value) => Boolean(value))
		if (!hasErrors) return

		return itineraryErrors
	}

	async getItinerary(itineraryId: string, invitationCode?: string): Promise<Itinerary> {
		const options: Record<string, string> = { itineraryId }
		if (invitationCode) {
			options.code = invitationCode
		}
		const res = await this.repository.getItinerary(options)
		const { data } = res
		return ItineraryEntityMapper.toItinerary(data)
	}

	async updateItineraryState(id: string, state: string): Promise<void> {
		await this.repository.edit(id, { state })
	}

	async getSpots(
		itineraryId: string,
		itineraryGroupId?: string | null,
		page?: number,
		pageSize?: number,
		code?: string,
		categoryIds?: string[]
	): Promise<any> {
		const response = await this.repository.getItinerarySpots({
			itineraryId,
			page,
			pageSize,
			code,
			itineraryGroupId,
			categoryIds,
		})
		const results = response?.data?.results
		return ItineraryEntityMapper.parseItinerarySpots(results)
	}

	async setSpotGroup(
		itineraryId: string,
		spotId: string,
		groupId: string,
		isSpotReference?: boolean
	) {
		await this.repository.aggregateSpots(itineraryId, [
			{
				spotId,
				groupsIds: [groupId],
				isReference: isSpotReference,
			},
		])
	}

	async removeSpotFromGroup(itineraryId: string, spotId: string, isSpotReference?: boolean) {
		await this.repository.aggregateSpots(itineraryId, [
			{
				spotId,
				groupsIds: [],
				isReference: isSpotReference,
			},
		])
	}

	async deleteGroup(itineraryId: string, groupId: string) {
		await this.repository.deleteGroup({ itineraryId, id: groupId })
	}

	public static async getMarkers(
		itineraryId: string,
		groupId?: string,
		itineraryMetadata?: boolean
	): Promise<Marker2[]> {
		return await ItineraryRepository.getMarkers(itineraryId, groupId, itineraryMetadata)
	}

	public static async addMedia(itineraryId: string, mediaId: string): Promise<Media> {
		return await ItineraryRepository.addMedia(itineraryId, mediaId)
	}

	public static async getMedias(itineraryId: string): Promise<Page<Media>> {
		return await ItineraryRepository.getMedias(itineraryId)
	}

	public static async deleteMedia(itineraryId: string, mediaId: string): Promise<void> {
		return await ItineraryRepository.deleteMedia(itineraryId, mediaId)
	}

	public static async getDestinations(itineraryId: string): Promise<Destination2[]> {
		return await ItineraryRepository.getDestinations(itineraryId)
	}

	public static async getTripOptions(
		itineraryId: string,
		invitationCode?: string
	): Promise<TripOptions> {
		return await ItineraryRepository.getItineraryOptions(itineraryId, invitationCode)
	}

	public static async getItinerary(itineraryId: string): Promise<Itinerary2> {
		return await ItineraryRepository.getItinerary(itineraryId)
	}

	public static async getInvitationTrip(
		tripId: string,
		invitationCode?: string
	): Promise<Itinerary2> {
		return await ItineraryRepository.getInvitationTrip(tripId, invitationCode)
	}

	public static async getSpots(
		itineraryId: string,
		page: number,
		groupId?: string
	): Promise<Page<Spot2>> {
		return await ItineraryRepository.getSpots(itineraryId, page, groupId)
	}

	public static async getSpot(itineraryId: string, spotId: string): Promise<ItinerarySpot> {
		return await ItineraryRepository.getSpot(itineraryId, spotId)
	}

	public static async deleteSpot(itineraryId: string, spotId: string): Promise<void> {
		return await ItineraryRepository.deleteSpot(itineraryId, spotId)
	}

	public static async addSpot(itineraryId: string, spotId: string): Promise<Spot2[]> {
		return await ItineraryRepository.saveSpots(itineraryId, [spotId])
	}

	public static async addSpots(
		itineraryId: string,
		spotIds: string[],
		collectionId?: string
	): Promise<Spot2[]> {
		return await ItineraryRepository.saveSpots(
			itineraryId,
			spotIds,
			undefined,
			undefined,
			collectionId
		)
	}

	public static async updateSpot(
		itineraryId: string,
		spotId: string,
		groupId: string
	): Promise<Spot2> {
		const response = await ItineraryRepository.saveSpots(itineraryId, [spotId], [groupId])
		return response[0]!
	}

	public static async deleteGroupFromSpot(itineraryId: string, spotId: string): Promise<Spot2> {
		const response = await ItineraryRepository.saveSpots(itineraryId, [spotId], [])
		return response[0]!
	}

	public static async getTravelers(itineraryId: string): Promise<Traveler2[]> {
		return await ItineraryRepository.getTravelers(itineraryId)
	}

	static canEdit(travelers: Traveler2[], profileId: string): boolean {
		const traveler = travelers.find((traveler) => traveler.id === profileId)
		if (!traveler) return false
		return true
	}

	public static async editItinerary(itinerary: Itinerary2): Promise<Itinerary2> {
		return await ItineraryRepository.editItinerary(itinerary)
	}

	static async editItineraryCover(
		itineraryId: string,
		mediaId: string | null
	): Promise<Itinerary2> {
		return await ItineraryRepository.editItineraryCover(itineraryId, mediaId)
	}

	public static async deleteTraveler(itineraryId: string, travelerId: string): Promise<void> {
		await ItineraryRepository.deleteTraveler(itineraryId, travelerId)
	}

	public static async deletePotentialTraveler(itineraryId: string): Promise<void> {
		await ItineraryRepository.deletePotentialTraveler(itineraryId)
	}

	public static async getGroups(itineraryId: string): Promise<ItineraryGroup[]> {
		return await ItineraryRepository.getGroups(itineraryId)
	}

	public static async createGroup(
		itineraryId: string,
		group: ItineraryGroup
	): Promise<ItineraryGroup> {
		return await ItineraryRepository.createGroup(itineraryId, group)
	}

	public static async editGroup(
		itineraryId: string,
		groupId: string,
		group: ItineraryGroup
	): Promise<ItineraryGroup> {
		return await ItineraryRepository.editGroup(itineraryId, groupId, group)
	}

	public static async deleteGroup(itineraryId: string, groupId: string): Promise<void> {
		return await ItineraryRepository.deleteGroup(itineraryId, groupId)
	}

	public static async getRecommendedSpots(itineraryId: string): Promise<Spot2[]> {
		return await ItineraryRepository.getRecommendedSpots(itineraryId)
	}

	public static async getMostSavedSpots(
		itineraryId: string,
		page: number = 0,
		pageSize: number = 10
	): Promise<Page<Spot2>> {
		return await ItineraryRepository.getMostSavedSpots(itineraryId, page, pageSize)
	}

	public static async addTask(itineraryId: string, task: TripTask): Promise<TripTask> {
		return await ItineraryRepository.createTripTask(itineraryId, task)
	}

	public static async addMultipleTasks(
		itineraryId: string,
		tasks: TripTask[],
		state: TRIP_TASK_STATE
	): Promise<TripTask[]> {
		return await ItineraryRepository.createMultipleTripTasks(itineraryId, tasks, state)
	}

	public static async getTask(itineraryId: string, taskId: string): Promise<TripTask> {
		return await ItineraryRepository.getTripTask(itineraryId, taskId)
	}

	public static async editTask(
		itineraryId: string,
		taskId: string,
		taskData: Partial<TripTask>
	): Promise<TripTask> {
		return await ItineraryRepository.editTask(itineraryId, taskId, taskData)
	}

	public static async assignTask(
		itineraryId: string,
		taskId: string,
		assignees: Traveler2[]
	): Promise<Traveler2[]> {
		return await ItineraryRepository.assignTask(itineraryId, taskId, assignees)
	}

	static async retrieveTaskAssignees(itineraryId: string, taskId: string): Promise<Traveler2[]> {
		return await ItineraryRepository.retrieveTaskAssignees(itineraryId, taskId)
	}

	public static async removeTask(itineraryId: string, taskId: string): Promise<void> {
		await ItineraryRepository.removeTask(itineraryId, taskId)
	}

	static async obtainTasksFrom(
		itineraryId: string,
		type: TRIP_TASK_STATE,
		page: number
	): Promise<Page<TripTask>> {
		return await ItineraryRepository.getTasksFrom(itineraryId, type, page)
	}

	static async getDays(itineraryId: string): Promise<ItineraryDay[]> {
		return await ItineraryRepository.getDays(itineraryId)
	}

	static async updateDay(itineraryId: string, day: Partial<ItineraryDay>): Promise<ItineraryDay> {
		return await ItineraryRepository.updateDay(itineraryId, day)
	}

	static async getDaySpots(itineraryId: string, day: number): Promise<Spot2[]> {
		return await ItineraryRepository.getDaySpots(itineraryId, day)
	}

	static async addSpotToDay(
		itineraryId: string,
		day: number,
		spotId: string,
		timespanId?: string,
		order?: number
	): Promise<Spot2> {
		const spots = await ItineraryRepository.addSpotsToDay(
			itineraryId,
			day,
			[spotId],
			timespanId,
			order
		)
		return spots[0]!
	}

	static async updateSpotInDay(
		itineraryId: string,
		day: number,
		spotId: string,
		timespanId?: string,
		order?: number
	): Promise<Spot2> {
		return await ItineraryRepository.updateSpotInDay(itineraryId, day, spotId, timespanId, order)
	}

	static async removeSpotFromDay(itineraryId: string, day: number, spotId: string): Promise<void> {
		await ItineraryRepository.deleteSpotFromDay(itineraryId, day, spotId)
	}

	static async orderSpotFromDay(
		itineraryId: string,
		day: number,
		spotId: string,
		order: number
	): Promise<Spot2[]> {
		return await ItineraryRepository.orderSpotFromDay(itineraryId, day, spotId, order)
	}

	private static retrieveItineraryNameErrors(itinerary: Itinerary2): string {
		if (!itinerary.name) return 'empty_value'
		if (!TextValidator.itineraryName(itinerary.name)) return 'invalid_format'
		return ''
	}

	private static retrieveItineraryDestinationsErrors(itinerary: Itinerary2): string {
		if (!itinerary.destination) return 'empty_value'
		return ''
	}

	static validateTask(task: TripTask): Partial<Record<keyof TripTask, string>> | undefined {
		const taskErrors: Partial<Record<keyof TripTask, string>> = {}
		if (!task.title) taskErrors.title = 'empty_value'

		const hasErrors = Object.values(taskErrors).some((value) => Boolean(value))
		if (!hasErrors) return
		return taskErrors
	}

	static async updatedDay(
		itineraryId: string,
		itineraryDay: Partial<ItineraryDay>
	): Promise<ItineraryDay> {
		return await ItineraryRepository.updatedDay(itineraryId, itineraryDay)
	}

	static async getDayMarkers(itineraryId: string, dayNumbers?: number[]): Promise<Marker2[]> {
		return ItineraryRepository.getDayMarkers(itineraryId, dayNumbers)
	}

	static async getTasksCountByState(tripId: string): Promise<Record<string, number>> {
		return await ItineraryRepository.getTasksCountByState(tripId)
	}

	static async getTrips(
		phase: TRIP_PHASE,
		userId: string,
		page: number,
		pageSize: number
	): Promise<Page<Itinerary2>> {
		let order: string = ORDER.MODIFIED_DATE_DESC
		if (phase === TRIP_PHASE.PAST) order = ORDER.END_DATE_ASC
		if (phase === TRIP_PHASE.NEXT) order = ORDER.START_DATE_ASC
		if (phase === TRIP_PHASE.NO_DATES) order = ORDER.CREATION_DATE_DESC
		return await ItineraryRepository.getTrips(phase, userId, order, page, pageSize)
	}

	static async getProducts(
		tripId: string,
		order: PRODUCT_ORDER = PRODUCT_ORDER.FEATURED,
		page: number = 0,
		pageSize: number = 10,
		filters: ProductFilters
	): Promise<Page<Product>> {
		return await ItineraryRepository.getProducts(tripId, order, page, pageSize, filters)
	}

	static async saveProduct(itineraryId: string, productId: string): Promise<void> {
		await ItineraryRepository.saveProduct(itineraryId, productId)
	}

	static async removeProduct(itineraryId: string, productId: string): Promise<void> {
		await ItineraryRepository.removeProduct(itineraryId, productId)
	}
}
